/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Account Cashback Control types
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.12.0
 */

import { IAction, IActionError, IActionWithPayload } from '../../../modules/app/createAction';
import {
    CASHBACK_GET_SERVICE_LIST_FAILURE,
    CASHBACK_GET_SERVICE_LIST_START,
    CASHBACK_GET_SERVICE_LIST_SUCCESS,
    CASHBACK_RESERVE_FAILURE,
    CASHBACK_RESERVE_START,
    CASHBACK_RESERVE_SUCCESS,
    CASHBACK_TOGGLE_AUTO_SPENDING_FAILURE,
    CASHBACK_TOGGLE_AUTO_SPENDING_START,
    CASHBACK_TOGGLE_AUTO_SPENDING_SUCCESS,
} from '../pageCashbackConstants';
import { IPageCashbackReducer } from '../pageCashbackReducer';
import { ICashbackMeta, TCashbackGetMetaResponse } from './cashbackTypes';

export interface ICashbackControlService {
    description: string;
    id: number;
    linkMore?: string;
    loyaltyRewardId: number; // id для резервирования
    title: string;
}

export interface ICashbackControlAvailableService extends ICashbackControlService {
    activationLink: string;
}

export interface ICashbackControlConnectedService extends ICashbackControlService {
    scoreReserved: number;
}

export enum ECashbackService {
    available = 'available',
    connected = 'connected',
}

/*
* -------------------------------------------
*     Get Service list
* -------------------------------------------
*/

export type TCashbackGetServiceListStartAction = IAction<typeof CASHBACK_GET_SERVICE_LIST_START>;

export type TCashbackGetServiceListSuccessPayload =
    Pick<IPageCashbackReducer, 'controlServiceListAvailable' | 'controlServiceListConnected'>;
export type TCashbackGetServiceListSuccessAction =
    IActionWithPayload<typeof CASHBACK_GET_SERVICE_LIST_SUCCESS, TCashbackGetServiceListSuccessPayload>;

export type TCashbackGetServiceListFailureAction =
    IActionError<typeof CASHBACK_GET_SERVICE_LIST_FAILURE>;

export interface ICashbackServiceListItemResponse {
    loyaltyRewardId: number;
    pdfLink?: string;
    shortDescription: string;
    title: string;
    weight: number;
    wwwLink?: string;
}

export interface ICashbackServiceListAvailableResponse extends ICashbackServiceListItemResponse {
    activationLink: string;
}

export interface ICashbackServiceListConnectedResponse extends ICashbackServiceListItemResponse {
    balanceReserved: number;
}

export interface ICashbackServiceListResponse {
    available: ICashbackServiceListAvailableResponse[];
    connected: ICashbackServiceListConnectedResponse[];
}

/*
* -------------------------------------------
*     Reserve scores for the service
* -------------------------------------------
*/

export enum ECashbackReserveStatus {
    error = 'error',
    processing = 'processing',
    start = 'start',
    success = 'success',
}

export interface ICashbackReserveStartPayload {
    amount: number;
    loyaltyRewardId: number;
}

export type TCashbackReserveStartAction =
    IActionWithPayload<typeof CASHBACK_RESERVE_START, ICashbackReserveStartPayload>;

export type TCashbackReserveResponse = TCashbackReserveSuccessPayload;
export type TCashbackReserveSuccessPayload = Pick<ICashbackMeta, 'balance' | 'balanceReserved' | 'balanceLost'>;
export type TCashbackReserveSuccessAction =
    IActionWithPayload<typeof CASHBACK_RESERVE_SUCCESS, TCashbackReserveSuccessPayload>;

export type TCashbackReserveFailureAction = IActionWithPayload<typeof CASHBACK_RESERVE_FAILURE, string[]>;


/*
* -------------------------------------------
*     Toggle Auto Spending
* -------------------------------------------
*/

export type TCashbackToggleAutoSpendingPayload = Pick<ICashbackMeta, 'isAutoSpendingEnabled'>;

export type TCashbackToggleAutoSpendingStartAction =
    IActionWithPayload<typeof CASHBACK_TOGGLE_AUTO_SPENDING_START, TCashbackToggleAutoSpendingPayload>;

export type TCashbackToggleAutoSpendingResponse = Pick<TCashbackGetMetaResponse, 'autoSpendingBonusesEnabled'>;

export type TCashbackToggleAutoSpendingSuccessAction =
    IActionWithPayload<typeof CASHBACK_TOGGLE_AUTO_SPENDING_SUCCESS, TCashbackToggleAutoSpendingPayload>;

export type TCashbackToggleAutoSpendingFailureAction = IActionError<typeof CASHBACK_TOGGLE_AUTO_SPENDING_FAILURE>;


/*
* -------------------------------------------
*       All types for control tab
* -------------------------------------------
*/

export type TCashbackControlActions =
    | TCashbackGetServiceListFailureAction
    | TCashbackGetServiceListStartAction
    | TCashbackGetServiceListSuccessAction
    | TCashbackReserveFailureAction
    | TCashbackReserveStartAction
    | TCashbackReserveSuccessAction
    | TCashbackToggleAutoSpendingFailureAction
    | TCashbackToggleAutoSpendingStartAction
    | TCashbackToggleAutoSpendingSuccessAction
    ;
